import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Phone, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import './Terms.css';

const SECTIONS = [
  {
    id: 'introduction',
    label: 'Introduction',
    number: null,
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure & Installation',
    number: null,
  },
  {
    id: 'ownership',
    label: 'Ownership & Licensing',
    number: null,
  },
  {
    id: 'subscription',
    label: 'Subscription & Billing',
    number: null,
  },
  {
    id: 'customization',
    label: 'Customization & Add-ons',
    number: null,
  },
  {
    id: 'refund',
    label: 'Refund & Payment Policy',
    number: null,
  },
  {
    id: 'liability',
    label: 'Responsibility & Liability',
    number: null,
  },
  {
    id: 'legal',
    label: 'Legal & Compliance',
    number: null,
  },
];

const CLAUSES = {
  infrastructure: [
    {
      n: '01',
      text: 'Server infrastructure required to run SHIVKMS software shall be arranged by the Client. SHIVKMS (hereinafter "SHIVKMS") will provide full assistance during the initial installation process. Minimum hardware and OS specifications are published on our website and must be reviewed before purchase.',
    },
    {
      n: '02',
      text: 'SHIVKMS software operates on Microsoft SQL Server or MySQL database engines. The Client is responsible for obtaining, licensing, and maintaining the appropriate database engine edition as per the SHIVKMS technical specification document. SHIVKMS will not be liable for any incompatibility arising from the use of unsupported database versions or configurations.',
    },
    {
      n: '03',
      text: 'All software updates, patches, and new module releases are made available via the SHIVKMS client portal or authorized deployment channels. SHIVKMS does not dispatch physical media (CD/DVD/USB) for updates or installations. The Client must maintain a stable internet connection to receive updates, license renewals, and support services.',
    },
    {
      n: '04',
      text: 'Initial installation at the Client\'s primary location is the responsibility of SHIVKMS and is included in the one-time development and installation fee. Any subsequent re-installations, branch installations, or server migrations after the initial deployment will be carried out on a chargeable basis.',
    },
    {
      n: '05',
      text: 'Client-side network connectivity, LAN/WAN configuration, and endpoint device setup are the sole responsibility of the Client. SHIVKMS will provide a deployment kit and remote guidance; however, on-site branch support for locations outside Delhi NCR will attract additional travel and service charges.',
    },
    {
      n: '06',
      text: 'SHIVKMS strongly recommends the implementation of SSL/TLS certificates for all web-based deployments of our applications. SHIVKMS shall bear no responsibility for data exposure, interception, or breach resulting from the Client\'s failure to implement or renew SSL/TLS security.',
    },
  ],
  ownership: [
    {
      n: '07',
      text: 'SHIVKMS retains full and exclusive ownership of all software, including but not limited to the application code, executable files, compiled binaries, user interface designs, database schema structure, stored procedures, triggers, and all associated intellectual property. The Client is granted a non-exclusive, non-transferable right to use the software solely for their internal business operations.',
    },
    {
      n: '08',
      text: 'Source code is the proprietary asset of SHIVKMS and is not included in any purchase, subscription, or licensing agreement. Under no circumstances will SHIVKMS sell, transfer, escrow, or disclose the source code to the Client or any third party. The Client acquires a right to use the software — not ownership of the software itself.',
    },
    {
      n: '09',
      text: 'The database schema, table structures, stored procedures, views, and any SQL Server or MySQL objects created by SHIVKMS during installation remain the intellectual property of SHIVKMS. Any unauthorized access to, modification of, export of, or replication of the database schema by the Client or any third party is strictly prohibited and constitutes a violation of intellectual property law under the Information Technology Act, 2000 and applicable copyright statutes.',
    },
    {
      n: '10',
      text: 'The Client shall not copy, reproduce, reverse-engineer, decompile, disassemble, or attempt to derive the source code or underlying logic of any SHIVKMS software product. Violation of this clause will result in immediate termination of the license and may attract legal proceedings.',
    },
    {
      n: '11',
      text: 'Ownership of the software license is non-transferable. The software may not be resold, sublicensed, rented, leased, or otherwise transferred to any third party without the express written consent of SHIVKMS. Transfer of business ownership does not automatically transfer software rights; the incoming entity must enter into a new agreement with SHIVKMS.',
    },
    {
      n: '12',
      text: 'SHIVKMS strictly prohibits the engagement, employment, or solicitation of any SHIVKMS employee or contractor by the Client, directly or indirectly, without prior written consent from SHIVKMS management. SHIVKMS reserves the right to terminate the service agreement with immediate effect and seek damages in the event of such solicitation.',
    },
  ],
  subscription: [
    {
      n: '13',
      text: 'SHIVKMS software products are offered on a subscription-based model. Following the one-time initial cost (covering development, customization, new add-ons, deployment, and installation), an Annual Maintenance and Subscription Charge (AMSC) becomes due exactly twelve (12) months from the date of installation or go-live, whichever is earlier.',
    },
    {
      n: '14',
      text: 'The initial one-time cost is higher than the recurring annual charge. This reflects the significant investment in custom development, module configuration, data migration, staff training, and the technical effort involved in deploying a production-ready system tailored to the Client\'s requirements. This fee is non-negotiable and non-refundable once development has commenced.',
    },
    {
      n: '15',
      text: 'The Annual Maintenance and Subscription Charge (AMSC) covers: software updates and enhancements released during the subscription period, technical support (remote assistance), license key renewals, and access to the client portal. The AMSC does not cover new modules, major version upgrades classified as a new product, or on-site support visits.',
    },
    {
      n: '16',
      text: 'If the AMSC is not received by SHIVKMS within thirty (30) days of the due date, a late payment surcharge of 2% per month will be levied on the outstanding amount, calculated from the original due date.',
    },
    {
      n: '17',
      text: 'If the AMSC remains unpaid beyond sixty (60) days from the due date, SHIVKMS reserves the right to suspend all services, including but not limited to: software updates, license renewals, technical support, and portal access. The software will continue to function in its last active state but no support or updates will be provided until dues are cleared in full, including accumulated late charges.',
    },
    {
      n: '18',
      text: 'If the AMSC and any outstanding dues remain unpaid beyond ninety (90) days from the due date, SHIVKMS reserves the right to deactivate the software license entirely. Reactivation after license deactivation will be subject to a reinstatement fee in addition to all outstanding dues, at SHIVKMS\'s discretion.',
    },
    {
      n: '19',
      text: 'SHIVKMS reserves the right to revise the Annual Maintenance and Subscription Charge annually, reflecting changes in operational costs, regulatory requirements, technology upgrades, and market conditions. Clients will be notified of any revision at least thirty (30) days prior to their renewal date.',
    },
    {
      n: '20',
      text: '100% of the one-time initial cost must be received by SHIVKMS before development begins or installation is carried out, unless a specific milestone-based payment schedule has been agreed upon in writing and signed by an authorized representative of SHIVKMS. Verbal or email commitments do not constitute a payment schedule.',
    },
    {
      n: '21',
      text: 'All invoices are payable via bank transfer (NEFT/RTGS/IMPS), UPI, or such payment gateways as designated by SHIVKMS. SHIVKMS does not accept cash payments unless accompanied by an official cash receipt voucher issued, signed, and stamped by SHIVKMS\'s accounts department. SHIVKMS shall not be held responsible for cash payments made without such a receipt.',
    },
  ],
  customization: [
    {
      n: '22',
      text: 'Any customization, enhancement, or functional modification requested by the Client that was not explicitly agreed upon and documented at the time of the original purchase will be treated as a new work order and billed separately. The minimum charge for any such modification is ₹25,000 (Rupees Twenty-Five Thousand) plus applicable GST, or may vary according to the scope of the change.',
    },
    {
      n: '23',
      text: 'New add-on modules, integrations with third-party systems, mobile application features, or API connectors not included in the original scope will be quoted and invoiced as separate line items. Acceptance of a formal quotation in writing constitutes agreement to the additional cost.',
    },
    {
      n: '24',
      text: 'SHIVKMS does not provide individual or standalone executable files for client-side testing or modification. All testing and quality assurance is conducted within the SHIVKMS controlled environment. Any separately provided test builds remain the property of SHIVKMS and must be used only for the purpose for which they were provided.',
    },
    {
      n: '25',
      text: 'SHIVKMS does not provide printed or electronic user manuals as a standard deliverable, as software features are subject to continuous improvement and regulatory updates. Video guides, contextual help documentation, and on-screen tooltips are provided within the application and via the SHIVKMS client portal.',
    },
    {
      n: '26',
      text: 'Any third-party SQL scripts, database modifications, or configuration changes applied directly to the Client\'s SQL Server or MySQL instance that have not been explicitly approved in writing by SHIVKMS management will not be supported by SHIVKMS. SHIVKMS accepts no responsibility for any data corruption, performance degradation, or system instability arising from unauthorized database changes.',
    },
  ],
  refund: [
    {
      n: '27',
      text: 'All payments made towards the one-time initial development and installation cost are strictly non-refundable once development work has commenced. Commencement of development is defined as the date on which SHIVKMS allocates engineering resources to the Client\'s project, which typically occurs within five (5) business days of receipt of the initial payment.',
    },
    {
      n: '28',
      text: 'Annual Maintenance and Subscription Charges, once paid, are non-refundable under any circumstances, including but not limited to: early termination of the agreement by the Client, change of business, closure of business, or migration to a competing product.',
    },
    {
      n: '29',
      text: 'In the event of a duplicate or erroneous payment (i.e., the same invoice paid twice), the excess amount will be verified and refunded to the Client via the same payment channel within fifteen (15) to twenty (20) working days of the confirmed duplicate being identified. The Client must notify SHIVKMS in writing within seven (7) days of the duplicate transaction occurring.',
    },
    {
      n: '30',
      text: 'SHIVKMS will issue a formal GST-compliant tax invoice for every transaction. Receipts generated by payment gateways serve as transaction confirmations and do not replace the official invoice issued by SHIVKMS.',
    },
  ],
  liability: [
    {
      n: '31',
      text: 'The Client bears sole responsibility for maintaining regular, verified backups of all data stored within the SQL Server or MySQL database. SHIVKMS provides best-practice backup guidance during onboarding but is not responsible for data loss resulting from hardware failure, accidental deletion, ransomware, or any other cause outside of a defect in the SHIVKMS software itself.',
    },
    {
      n: '32',
      text: 'SHIVKMS shall not be liable for any direct, indirect, incidental, consequential, or punitive damages — including loss of business, loss of profit, regulatory penalties, or reputational damage — arising from the use of, or inability to use, SHIVKMS software. The Client uses the software at their own risk and accepts that no software system is entirely free from defects.',
    },
    {
      n: '33',
      text: 'SHIVKMS is not responsible for system downtime, data access failure, or service interruption caused by third-party infrastructure, including but not limited to: cloud hosting providers, internet service providers, SQL Server or MySQL engine failures, operating system failures, or network outages within the Client\'s environment.',
    },
    {
      n: '34',
      text: 'SHIVKMS does not warrant that the software will meet every specific requirement of the Client beyond what was agreed in the original scope of work. The Client is responsible for adequately evaluating the software during the demonstration and trial phases before completing payment.',
    },
    {
      n: '35',
      text: 'SHIVKMS will not be held responsible for compliance failures, regulatory penalties, or legal consequences arising from the Client\'s incorrect use of the software, failure to update records in a timely manner, or failure to follow applicable laws and regulations governing their industry.',
    },
    {
      n: '36',
      text: 'In no event shall SHIVKMS\'s total liability to the Client for any claim arising out of or related to this agreement exceed the total AMSC paid by the Client to SHIVKMS in the twelve (12) months immediately preceding the event giving rise to the claim.',
    },
  ],
  legal: [
    {
      n: '37',
      text: 'This agreement constitutes an electronic record within the meaning of the Information Technology Act, 2000 and the rules framed thereunder. By registering, purchasing, or using any SHIVKMS product or service, the Client agrees to be legally bound by these Terms and Conditions.',
    },
    {
      n: '38',
      text: 'All disputes, claims, or controversies arising out of or relating to these Terms, the software, or the services provided by SHIVKMS shall be subject exclusively to the jurisdiction of the courts in New Delhi, India. The governing law shall be the laws of the Republic of India.',
    },
    {
      n: '39',
      text: 'Verbal commitments, WhatsApp messages, or informal email discussions with any SHIVKMS representative — including sales, support, or technical staff — do not constitute a binding amendment to these Terms. Any modification to these Terms or to a Client\'s service agreement must be documented in a formal written addendum signed by an authorized officer of SHIVKMS.',
    },
    {
      n: '40',
      text: 'By registering or using SHIVKMS products and services, the Client provides explicit consent for SHIVKMS to contact them via telephone, email, WhatsApp, or other electronic communication channels for the purposes of support, billing, product updates, and marketing of SHIVKMS products.',
    },
    {
      n: '41',
      text: 'SHIVKMS reserves the right to amend these Terms and Conditions at any time. Clients will be notified of material changes via email or the client portal. Continued use of SHIVKMS software following such notification constitutes acceptance of the revised Terms.',
    },
    {
      n: '42',
      text: 'If any provision of these Terms is found to be invalid, unlawful, or unenforceable by a court of competent jurisdiction, that provision shall be deemed modified to the minimum extent necessary to make it enforceable. The remaining provisions shall continue in full force and effect.',
    },
  ],
};

const REF_NO = 'SHIVKMS/TC/2025/01';
const LAST_UPDATED = '1st January, 2025';

function SectionLink({ id, label, active, onClick }) {
  return (
    <button
      className={`terms-nav-link ${active ? 'active' : ''}`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );
}

export default function Terms() {
  const [activeSection, setActiveSection] = useState('introduction');

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleScroll = (e) => {
    const container = e.currentTarget;
    const sections = SECTIONS.map(s => document.getElementById(s.id)).filter(Boolean);
    let current = sections[0]?.id;
    for (const el of sections) {
      if (el.getBoundingClientRect().top - 160 <= 0) current = el.id;
    }
    setActiveSection(current);
  };

  return (
    <div className="terms-page">
      <SEO
        title="Terms & Conditions"
        description="Legal terms and conditions for the use of SHIVKMS software, subscription services and support. Read our licensing, billing, refund and liability policies."
        keywords="SHIVKMS terms and conditions, software license agreement, subscription billing policy, SHIVKMS legal"
        path="/terms"
      />

      {/* Hero */}
      <section className="terms-hero">
        <div className="terms-hero-bg" />
        <div className="container">
          <div className="terms-hero-inner">
            <span className="eyebrow">Legal Documentation</span>
            <h1>Terms & <span className="gradient-text">Conditions</span></h1>
            <p>Operating procedures and legal framework for Shivkms Pvt Ltd (SKMS PVT LTD)</p>
            <div className="terms-meta">
              <span>Ref No: <strong>{REF_NO}</strong></span>
              <span className="terms-meta-sep">·</span>
              <span>Last updated: <strong>{LAST_UPDATED}</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="terms-body section-py">
        <div className="container">
          <div className="terms-layout">

            {/* Sticky sidebar */}
            <aside className="terms-sidebar">
              <p className="terms-sidebar-title">Contents</p>
              <nav className="terms-nav">
                {SECTIONS.map(s => (
                  <SectionLink
                    key={s.id}
                    id={s.id}
                    label={s.label}
                    active={activeSection === s.id}
                    onClick={scrollTo}
                  />
                ))}
              </nav>
              <div className="terms-support-card">
                <p className="terms-support-title">Need Clarification?</p>
                <p className="terms-support-sub">Our team is available for any legal or technical questions.</p>
                <a href="tel:+919212552928" className="terms-support-link">
                  <Phone size={14} /> +91 9212552928
                </a>
                <a href="mailto:contact@shivkms.com" className="terms-support-link">
                  <Mail size={14} /> contact@shivkms.com
                </a>
                <Link to="/contact" className="btn-primary terms-support-btn">
                  Contact Support →
                </Link>
              </div>
            </aside>

            {/* Main content */}
            <div className="terms-content" onScroll={handleScroll}>

              {/* Introduction */}
              <div id="introduction" className="terms-section">
                <h2 className="terms-section-title">Introduction</h2>
                <p className="terms-intro-text">
                  This document is an electronic record within the meaning of the Information Technology Act, 2000 and the rules framed thereunder. It constitutes a legally binding agreement between <strong>SHIVKMS</strong> (hereinafter referred to as <strong>"SHIVKMS"</strong> or <strong>"SKMS"</strong>) and the individual, business entity, institution, or organization (hereinafter referred to as the <strong>"Client"</strong>) accessing, purchasing, installing, or using any software product, web application, mobile application, or associated service offered by SHIVKMS.
                </p>
                <p className="terms-intro-text">
                  By registering, making any payment, or using any SHIVKMS product or service, the Client acknowledges that they have read, understood, and agreed to be bound by all clauses set out herein. If the Client does not agree to these Terms, they must not use SHIVKMS products or services.
                </p>
                <div className="terms-highlight-box">
                  <p><strong>Important:</strong> SHIVKMS software is provided on a <strong>subscription-based model</strong>. The initial cost covers development, customization, installation, and onboarding. An annual subscription fee is charged every twelve months thereafter. Failure to pay the annual subscription will result in suspension and eventual deactivation of services. Please read Section 4 (Subscription & Billing) carefully.</p>
                </div>
              </div>

              {/* Infrastructure */}
              <div id="infrastructure" className="terms-section">
                <h2 className="terms-section-title">Infrastructure & Installation</h2>
                <div className="terms-clauses">
                  {CLAUSES.infrastructure.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ownership */}
              <div id="ownership" className="terms-section">
                <h2 className="terms-section-title">Ownership & Licensing</h2>
                <div className="terms-highlight-box terms-highlight-warning">
                  <p><strong>Note on Source Code:</strong> SHIVKMS does not sell, transfer, or disclose source code under any agreement. The Client receives a right to use the compiled software — not ownership of the underlying code, database schema, or intellectual property.</p>
                </div>
                <div className="terms-clauses">
                  {CLAUSES.ownership.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription */}
              <div id="subscription" className="terms-section">
                <h2 className="terms-section-title">Subscription & Billing</h2>
                <div className="terms-highlight-box">
                  <p><strong>Subscription Model:</strong> All SHIVKMS products operate on an annual subscription. The first subscription renewal is due exactly 12 months after installation. Non-payment results in service suspension after 60 days, and license deactivation after 90 days from the due date.</p>
                </div>
                <div className="terms-clauses">
                  {CLAUSES.subscription.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>

                {/* Visual timeline */}
                <div className="billing-timeline">
                  <p className="billing-timeline-title">Subscription Lifecycle</p>
                  <div className="billing-timeline-steps">
                    <div className="bts bts-active">
                      <span className="bts-dot" />
                      <span className="bts-label">Day 0</span>
                      <span className="bts-desc">Installation & Go-Live</span>
                    </div>
                    <div className="bts-line" />
                    <div className="bts bts-active">
                      <span className="bts-dot" />
                      <span className="bts-label">Month 12</span>
                      <span className="bts-desc">AMSC Due</span>
                    </div>
                    <div className="bts-line" />
                    <div className="bts bts-warn">
                      <span className="bts-dot warn" />
                      <span className="bts-label">+60 Days</span>
                      <span className="bts-desc">Services Suspended</span>
                    </div>
                    <div className="bts-line warn" />
                    <div className="bts bts-danger">
                      <span className="bts-dot danger" />
                      <span className="bts-label">+90 Days</span>
                      <span className="bts-desc">License Deactivated</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customization */}
              <div id="customization" className="terms-section">
                <h2 className="terms-section-title">Customization & Add-ons</h2>
                <div className="terms-clauses">
                  {CLAUSES.customization.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Refund */}
              <div id="refund" className="terms-section">
                <h2 className="terms-section-title">Refund & Payment Policy</h2>
                <div className="terms-clauses">
                  {CLAUSES.refund.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Liability */}
              <div id="liability" className="terms-section">
                <h2 className="terms-section-title">Responsibility & Liability</h2>
                <div className="terms-clauses">
                  {CLAUSES.liability.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Legal */}
              <div id="legal" className="terms-section">
                <h2 className="terms-section-title">Legal & Compliance</h2>
                <div className="terms-clauses">
                  {CLAUSES.legal.map(c => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>
                      <p className="clause-text">{c.text}</p>
                    </div>
                  ))}
                </div>

                <div className="terms-final-note">
                  <p><strong>Note:</strong> All clauses set out in this document are legally binding and form part of the agreement between SHIVKMS and the Client. For a fully executed service agreement, please contact SHIVKMS at <a href="mailto:contact@shivkms.com">contact@shivkms.com</a>.</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}